import { useMutation } from '@apollo/client/react'
import {
  Alert,
  Box,
  Button,
  Center,
  Checkbox,
  Heading,
  Input,
  InputProps,
  NativeSelect,
  SimpleGrid,
  Spinner,
  TextareaProps,
  VStack,
  Field,
} from '@chakra-ui/react'
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { ChevronRightIcon } from '@src/components/sco/icons'
import company_stages from '@src/constants/company-stages'
import funding_ranges from '@src/constants/funding-ranges'
import industries from '@src/constants/industries'
import topics from '@src/constants/topics'
import { UpsertFgProfilePayload } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { TrackEvent } from '@src/lib/TrackEvent'
import { gql } from '@src/lib/gql'
import tracker from '@src/lib/tracker'
import { useRouter } from 'next/navigation'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

type UserSignupProfileFormFields = {
  companyName: string
  companyIndustryKey: string
  companyFundingRaisedKey: string
  companyStageKey: string
  founderNeedsKeys: string[]
  founderNeedsOther: string
}

type UserSignupProfileFormProps = GenericFormProps<UserSignupProfileFormFields> & {
  title?: string
  returnToHref?: string
  hubspotFormKey?: string
}

export const UserSignupProfileForm = (props: UserSignupProfileFormProps) => {
  const router = useRouter()
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null)
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const form = useForm<UserSignupProfileFormFields>({
    mode: 'onSubmit',
    defaultValues: {
      companyName: currentUser?.company?.name,
      companyIndustryKey: currentUser?.company?.industryKey,
      companyFundingRaisedKey: currentUser?.company?.fundingRaisedKey,
      companyStageKey: currentUser?.company?.stageKey,
      founderNeedsKeys: currentUser?.founderNeedsKeys,
      founderNeedsOther: currentUser?.founderNeedsOther,
    },
  })

  const [upsertFGProfile, { loading, error }] = useMutation(gql`
    mutation ($input: UpsertFGProfileInput!) {
      upsertFGProfile(input: $input) {
        viewer {
          id
          currentUser {
            id
            founderNeedsKeys
            founderNeedsOther
            userId
            company {
              id
              fundingRaisedKey
              industryKey
              name
              stageKey
            }
          }
        }
      }
    }
  `)

  const handleSubmit = useCallback(
    async (values: UserSignupProfileFormFields) =>
      processSubmit({
        props,
        values,
        setFormErrorMessage,
        action: async (values: UserSignupProfileFormFields) => {
          return upsertFGProfile({
            variables: {
              input: {
                companyName: values.companyName,
                companyIndustry: values.companyIndustryKey,
                companyStage: values.companyStageKey,
                companyFundingRaised: values.companyFundingRaisedKey,
                founderNeeds: values.founderNeedsKeys || [],
                founderNeedsOther: values.founderNeedsOther,
                hubspotFormKey: props.hubspotFormKey,
              },
            },
          })
        },
        afterSuccess: async (values: UserSignupProfileFormFields, result: UpsertFgProfilePayload) => {
          await tracker.s2Submitted({ ...values })

          if (props.returnToHref) {
            setIsRedirecting(true)
            await refetchCurrentUser()
            router.push(props.returnToHref)
          } else {
            await refetchCurrentUser()
          }
        },
      }),
    []
  )

  if (!currentUser) {
    return (
      <FormContainer {...props.containerProps}>
        <Center>
          <Spinner />
        </Center>
      </FormContainer>
    )
  }

  return (
    <FormContainer {...props.containerProps}>
      <TrackEvent action={'s2Viewed'} onlyOnScreen={true} />
      {props.title && (
        <Heading marginBottom={'1em'} size={'lg'}>
          {props.title}
        </Heading>
      )}
      <VStack asChild align={'stretch'} gap={'1.5em'}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          {formErrorMessage && (
            <Alert.Root borderRadius={'md'} status={'error'}>
              <Alert.Indicator />
              <Box>{formErrorMessage}</Box>
            </Alert.Root>
          )}
          <Field.Root invalid={!!form.formState.errors.companyName}>
            <Field.Label>Startup Name</Field.Label>
            <Input
              autoFocus={true}
              placeholder={'Acme Inc.'}
              type={'text'}
              {...(props.inputProps as InputProps)}
              {...form.register('companyName', {})}
            />
            <Field.ErrorText>{form.formState.errors.companyName?.message}</Field.ErrorText>
          </Field.Root>
          <SimpleGrid columns={2} gap={'1em'}>
            <Field.Root invalid={!!form.formState.errors.companyIndustryKey}>
              <Field.Label>Industry</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  autoFocus={true}
                  placeholder={'Select One'}
                  type={'text'}
                  {...(props.inputProps as React.SelectHTMLAttributes<HTMLSelectElement>)}
                  {...form.register('companyIndustryKey', {})}
                >
                  {industries.map((i, idx) => (
                    <option key={idx} value={i.key}>
                      {i.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.companyFundingRaisedKey?.message}</Field.ErrorText>
            </Field.Root>

            <Field.Root invalid={!!form.formState.errors.companyFundingRaisedKey}>
              <Field.Label>Funding Raised</Field.Label>
              <NativeSelect.Root>
                <NativeSelect.Field
                  autoFocus={true}
                  placeholder={'Select One'}
                  type={'text'}
                  {...(props.inputProps as React.SelectHTMLAttributes<HTMLSelectElement>)}
                  {...form.register('companyFundingRaisedKey', {})}
                >
                  {funding_ranges.map((fundingRange, idx) => (
                    <option key={idx} value={fundingRange.key}>
                      {fundingRange.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Field.ErrorText>{form.formState.errors.companyIndustryKey?.message}</Field.ErrorText>
            </Field.Root>
          </SimpleGrid>
          <Field.Root invalid={!!form.formState.errors.companyStageKey}>
            <Field.Label>Stage</Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                autoFocus={true}
                placeholder={'Select One'}
                type={'text'}
                {...(props.inputProps as React.SelectHTMLAttributes<HTMLSelectElement>)}
                {...form.register('companyStageKey', {})}
              >
                {company_stages.map((stage, idx) => (
                  <option key={idx} value={stage.key}>
                    {`${stage.name}: ${stage.description}`}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Field.ErrorText>{form.formState.errors.companyStageKey?.message}</Field.ErrorText>
          </Field.Root>
          <Field.Root invalid={!!form.formState.errors.founderNeedsKeys}>
            <Field.Label>
              {'What areas do you need help with? '}
              <small>Select all that apply.</small>
            </Field.Label>

            <SimpleGrid columns={[1, 1, 2]} gap={'0.25em'} padding={'0.25em'}>
              {topics
                .filter(t => t.isPrimary)
                .map((topic, idx) => {
                  return (
                    <Checkbox.Root
                      key={idx}
                      defaultChecked={!!currentUser?.founderNeedsKeys?.find(key => key == topic.key)}
                      value={topic.key}
                      {...form.register('founderNeedsKeys')}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Checkbox.Label>{topic.title}</Checkbox.Label>
                    </Checkbox.Root>
                  )
                })}
            </SimpleGrid>
            <Field.ErrorText>{form.formState.errors.companyStageKey?.message}</Field.ErrorText>
          </Field.Root>
          <Field.Root invalid={!!form.formState.errors.founderNeedsOther}>
            <Field.Label>What is your biggest challenge right now?</Field.Label>
            {/* @ts-ignore */}
            <AutoResizeTextarea
              {...(props.inputProps as TextareaProps)}
              {...form.register('founderNeedsOther', {})}
            />
            <Field.ErrorText>{form.formState.errors.founderNeedsOther?.message}</Field.ErrorText>
          </Field.Root>
          <Box paddingTop={'0.5em'}>
            <Button
              loading={form.formState.isSubmitting || isRedirecting}
              size={'lg'}
              type={'submit'}
              variant={'cta'}
              width={'full'}
              {...props.submitButtonProps}
            >
              Submit
              <ChevronRightIcon />
            </Button>
          </Box>
        </form>
      </VStack>
    </FormContainer>
  )
}
